import { DateTime } from "luxon";

// Display identities never replace canonical IDs. Ordinals distinguish otherwise
// identical labels in the supplied, consistently ordered collection.
export function readableOptions(rows, label) {
  const labels = rows.map(label),
    counts = new Map(),
    seen = new Map();
  for (const text of labels) counts.set(text, (counts.get(text) || 0) + 1);
  return rows.map((row, index) => {
    const text = labels[index],
      ordinal = (seen.get(text) || 0) + 1;
    seen.set(text, ordinal);
    return {
      value: row.id,
      label: text + (counts.get(text) > 1 ? ` (${ordinal})` : ""),
    };
  });
}
export function staffLabel(person, fallback) {
  return (
    [person.first_name, person.last_name].filter(Boolean).join(" ") +
    ` · ${person.job_title || fallback}`
  );
}
export function localTime(value, timezone) {
  return value
    ? DateTime.fromISO(value, { zone: timezone }).toFormat("yyyy-MM-dd'T'HH:mm")
    : "";
}
export function displayTime(value, locale, timezone) {
  if (!value) return "";
  const time = DateTime.fromISO(value, { zone: timezone });
  if (!time.isValid) return "";
  // Literal AM/PM is intentional, including Spanish, to make the scheduling
  // convention explicit on every device.
  return `${time.setLocale(locale).toLocaleString(DateTime.DATE_MED)} · ${time.toFormat("h:mm a")}`;
}
export function displaySession(start, end, locale, timezone) {
  const from = DateTime.fromISO(start, {zone:timezone});
  const to = DateTime.fromISO(end, {zone:timezone});
  if (!from.isValid || !to.isValid) return "";
  return `${displayTime(start,locale,timezone)}–${from.hasSame(to,"day") ? to.toFormat("h:mm a") : displayTime(end,locale,timezone)}`;
}
export function runOptions(offers, occurrences, tr, locale, timezone) {
  return readableOptions(offers, (offer) => {
    const first = occurrences
      .filter((c) => c.offering_id === offer.id && c.state !== "cancelled")
      .sort((a, b) => a.start_at.localeCompare(b.start_at))[0];
    const when = first
      ? displayTime(first.start_at, locale, offer.timezone || timezone)
      : tr(offer.scheduling_mode === "on_assignment" ? "datesWhenAssigning" : offer.state === "draft" ? "guided.draftLabel" : "notScheduled");
    const status =
      !first && offer.state === "draft"
        ? ""
        : ` · ${tr(offer.state === "published" ? "publishedState" : offer.state)}`;
    return `${offer.display_title||offer.snapshot.title} · ${when}${status}`;
  });
}
export function scheduleInitial(offer, assignments, occurrence = null) {
  const assigned = assignments.filter((a) => a.offering_id === offer.id);
  return {
    ...offer.scheduling_defaults,
    capacity: occurrence?.capacity || offer.capacity,
    instructor_id:
      occurrence?.instructor_id ||
      offer.scheduling_defaults?.instructor_id || (assigned.length === 1 ? assigned[0].staff_id : ""),
    location_kind: occurrence?.location_kind || "meeting_point",
    location_label: occurrence?.location_label || offer.scheduling_defaults?.location_label || "",
    reserve: occurrence?.location_kind === "club_space" || !!occurrence?.facility_reservation_id,
    existing_reservation: occurrence?.location_kind === "club_space" || !!occurrence?.facility_reservation_id,
    space_id: occurrence?.space_id || "",
    start_local: localTime(occurrence?.start_at, offer.timezone),
    end_local: localTime(occurrence?.end_at, offer.timezone),
    mode: "one_off",
    weeks: 4,
  };
}
export function schedulePayload(form) {
  return {
    start_local: form.start_local,
    end_local: form.end_local,
    instructor_id: form.instructor_id,
    capacity: Number(form.capacity),
    weeks: form.mode === "repeating" ? Number(form.weeks) : 1,
    location_label: form.location_label,
    location_kind: form.reserve ? "club_space" : "meeting_point",
    ...(form.reserve ? { space_id: form.space_id } : {}),
  };
}
export function scheduleErrors(form, timezone, tr) {
  const errors = {};
  const start = DateTime.fromISO(form.start_local, { zone: timezone }),
    end = DateTime.fromISO(form.end_local, { zone: timezone });
  for (const [key, time] of [
    ["start_local", start],
    ["end_local", end],
  ]) {
    if (!time.isValid || time.toFormat("yyyy-MM-dd'T'HH:mm") !== form[key])
      errors[key] = tr("errors.invalid_local_time");
    else if (time.getPossibleOffsets().length !== 1)
      errors[key] = tr("errors.ambiguous_local_time");
  }
  if (!errors.start_local && start.toMillis() <= Date.now())
    errors.start_local = tr("errors.occurrence_future_required");
  if (!errors.end_local && !errors.start_local && end <= start)
    errors.end_local = tr("guided.endAfterStart");
  if (!form.location_label?.trim())
    errors.location_label = tr("errors.location_required");
  if (form.reserve && !form.space_id)
    errors.space_id = tr("errors.facility_required");
  if (!form.instructor_id)
    errors.instructor_id = tr("errors.instructor_not_assigned");
  if (!Number.isInteger(Number(form.capacity)) || Number(form.capacity) < 1)
    errors.capacity = tr("errors.invalid_capacity");
  if (
    form.mode === "repeating" &&
    (!Number.isInteger(Number(form.weeks)) ||
      Number(form.weeks) < 1 ||
      Number(form.weeks) > 52)
  )
    errors.weeks = tr("errors.invalid_recurrence");
  if(scheduleWarnings(form,timezone).length&&!form.unusual_ack)errors.start_local=tr("guided.unusualAcknowledgement");
  return errors;
}

// Advisory thresholds: before 07:00, after 22:00, or more than eight hours.
export function scheduleWarnings(form,timezone) {
 const start=DateTime.fromISO(form.start_local,{zone:timezone}),end=DateTime.fromISO(form.end_local,{zone:timezone});
 if(!start.isValid||!end.isValid)return [];
 return [...(start.hour<7||start.hour>=22||end.hour<7||end.hour>=22?['hours']:[]),...(end.diff(start,'hours').hours>8?['duration']:[])];
}

export function friendlyDuration(minutes, locale = "en-GB") {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return "";
  const hours = Math.floor(value / 60), remainder = Math.round(value % 60);
  return [[hours, "hour"], [remainder, "minute"]]
    .filter(([count]) => count > 0)
    .map(([count, unit]) => new Intl.NumberFormat(locale, {style:"unit", unit, unitDisplay:"long"}).format(count))
    .join(" ");
}

export function displayDate(value, locale="en-GB") {
  if(!value)return "";
  const date=DateTime.fromISO(String(value).slice(0,10),{zone:"UTC"});
  return date.isValid?date.setLocale(locale).toLocaleString(DateTime.DATE_MED):"";
}

export function canRecordAttendance(occurrence, status, timezone, now = Date.now()) {
  if (!occurrence || occurrence.state === 'cancelled') return false;
  const start = DateTime.fromISO(occurrence.start_at, {zone: timezone});
  const current = DateTime.fromMillis(now, {zone: timezone});
  return start.isValid && current.isValid && (start.toMillis() <= now || status === 'present' && start.toISODate() === current.toISODate());
}
